/**
 * SchedaEmail — l'email che parte dopo l'invio a chi ha lasciato l'indirizzo. La impagina il
 * server col guscio unico delle email (marchio YumaOS + scheda); qui si scrivono solo le parole.
 * Nei testi si possono usare `{nome}` e `{azienda}`.
 */

import React from 'react';
import { Alert, FormControlLabel, Paper, Stack, Switch, Typography } from '@mui/material';
import { tokens } from '../../../theme';
import { CampoTesto } from './CampoTesto';
import { emailVuota, type Bozza } from './bozza';

interface Props {
  bozza: Bozza;
  onChange: (patch: Partial<Bozza>) => void;
}

export const SchedaEmail: React.FC<Props> = ({ bozza, onChange }) => {
  const email = bozza.email_conferma;
  const chiedeEmail = !!bozza.contatti?.campi.some((c) => c.id === 'email');
  const conConsenso = !!bozza.contatti?.consenso;
  const set = (patch: Partial<NonNullable<Bozza['email_conferma']>>) => email && onChange({ email_conferma: { ...email, ...patch } });

  return (
    <Paper variant="outlined" sx={{ p: 2, borderColor: tokens.color.line }}>
      <FormControlLabel
        control={(
          <Switch
            checked={!!email && email.attiva}
            onChange={(e) => onChange({ email_conferma: e.target.checked ? { ...(email ?? emailVuota()), attiva: true } : email ? { ...email, attiva: false } : null })}
          />
        )}
        label={<Typography variant="body2" fontWeight={700}>Manda l'email di conferma</Typography>}
      />
      {!chiedeEmail && (
        <Alert severity="info" sx={{ mt: 1 }}>L'ultima schermata non chiede l'email: per mandarla, aggiungi il campo email.</Alert>
      )}
      {email && email.attiva && (
        <Stack spacing={1.5} sx={{ mt: 1.5 }}>
          <Typography variant="caption" sx={{ color: tokens.color.ash }}>
            Si possono usare {'{nome}'} e {'{azienda}'}: si sostituiscono coi dati di chi ha risposto.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <CampoTesto label="Oggetto" value={email.oggetto} onChange={(v) => set({ oggetto: v })} />
            <CampoTesto label="Anteprima" value={email.anteprima} onChange={(v) => set({ anteprima: v })} helperText="La riga accanto all'oggetto nell'elenco della posta" />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <CampoTesto label="Scritta grande" value={email.in_grande} onChange={(v) => set({ in_grande: v })} helperText="Es. «Da marzo 2027»" />
            <CampoTesto label="Titolo" value={email.titolo} onChange={(v) => set({ titolo: v })} />
          </Stack>
          <CampoTesto label="Testo" value={email.testo} onChange={(v) => set({ testo: v })} multiline minRows={2} />
          <CampoTesto
            label="Titolo del riepilogo delle risposte"
            value={email.titolo_riepilogo}
            onChange={(v) => set({ titolo_riepilogo: v })}
            helperText="Es. «Cosa ci hai detto». Vuoto = niente riepilogo. Usa il nome breve delle domande"
          />
          {conConsenso && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <CampoTesto
                label="Testo sopra il bottone di conferma degli aggiornamenti"
                value={email.testo_consenso}
                onChange={(v) => set({ testo_consenso: v })}
                multiline
                helperText="Lo vede solo chi ha spuntato il consenso"
              />
              <CampoTesto label="Bottone di conferma" value={email.bottone_consenso} onChange={(v) => set({ bottone_consenso: v })} helperText="Es. «Conferma gli aggiornamenti»" />
            </Stack>
          )}
          <CampoTesto label="Perché la si riceve" value={email.perche} onChange={(v) => set({ perche: v })} multiline minRows={2} />
          <CampoTesto label="Collegamento per uscire" value={email.etichetta_uscita} onChange={(v) => set({ etichetta_uscita: v })} helperText="Es. «Esci dalla lista d'attesa». C'è sempre" />
        </Stack>
      )}
    </Paper>
  );
};
